import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import shallow from "zustand/shallow";
import useStore from "@/stores/index";
import { getMessagesForThreadKey } from "@/stores/chatStore";
import { classroomActiveInfoV2 } from "@/js/utils/classroomSessionUtils";
import { formatMsgTs } from "@/js/utils/chatUtils";
import { ClassroomOverviewDTO, ClassroomDetails } from "@/stores/classroomStore_I";
import { ChatMessage } from "../types";
import ChatInput from "./ChatInput";
import MessageItem from "./MessageItem";
import NoEntries from "./NoEntries";
import { useClassroomScreenshotDataStore } from "@/stores/v2/classroomScreenshotDataStore";
import { WindowData } from "../../RegularClassroomUser/utils";
import { useStudentClaimsQuery } from "@/query/studentClaimsQuery";

interface Props {
	/** Username of the selected student */
	username?: string;
	/** Classroom object containing class information */
	classroom?: ClassroomOverviewDTO;
	/** Thread key for the conversation */
	threadKey?: string;
	/** Whether chat is blocked for this classroom */
	chatBlocked: boolean;
	/** Array of avatar colors for different users */
	avatarColors?: string[] | Record<string, string>;
	/** Whether the component is embedded in another view */
	isEmbedded?: boolean;
	/** Function called to enable chat */
	handleChatEnable?: () => void;
}

const MessagePane: React.FC<Props> = ({
	username,
	classroom,
	threadKey,
	chatBlocked = false,
	avatarColors,
	isEmbedded,
	handleChatEnable,
}) => {
	const { t } = useTranslation();
	const [messages, setMessages] = useState<ChatMessage[]>([]);
	const [inputMessage, setInputMessage] = useState<string>("");
	const [isChatTurnedOff, setIsChatTurnedOff] = useState<boolean>(chatBlocked || false);
	const [classActive, setClassActive] = useState<boolean>(false);
	const [studentDeviceWindowData, setStudentDeviceWindowData] = useState<WindowData | null>(null);
	let isMonitoringReminderShown = false;

	const scrollRef = React.useRef<HTMLDivElement>(null);
	const conversationContainerRef = React.useRef<HTMLDivElement>(null);

	const classroomIdForChat = classroom?.name;
	// @ts-expect-error - Leaving code as it is
	const classroomDetails: ClassroomDetails = useStore.getState().classroom.getClassroomV2(classroom?.sourceId);

	const currentClassStableId = classroom?.stableIdV1 ?? classroomDetails?.stableIdV1;

	const studentClaimsQuery = useStudentClaimsQuery({
		classroomStableId: currentClassStableId as string,
	});

	// check if the selected student is claimed in the current class
	// if there are no claims for student we assume they are claimed
	// this can be due to claim students feature flag being off.
	const claimForSelectedStudent = studentClaimsQuery.data?.find((c) => c.studentUsername === username);
	const isStudentClaimed = claimForSelectedStudent ? Boolean(claimForSelectedStudent.classId === currentClassStableId) : true;

	const getWindowDataForUser = useClassroomScreenshotDataStore((state) => state.getWindowDataForUser);

	const handleNewMessagesReceived = (): void => {
		const newMessages: ChatMessage[] = getMessagesForThreadKey(useStore.getState(), threadKey);
		setMessages(newMessages);

		const filteredMsgs = newMessages.filter((msg) => !msg.isFakeMessage);

		// do not scroll if there are no messages are all
		if (!filteredMsgs.length) {
			return;
		}

		// auto scroll to bottom text only if not user initiated load by scrolling to top
		// @ts-expect-error - Leaving code as it is
		if (!useStore.getState().chat.scrollFetch && scrollRef.current) {
			setTimeout(() => {
				if (scrollRef && scrollRef.current) {
					scrollRef.current.scrollIntoView({ block: "end", behavior: "smooth" });
				}
			}, 1000);
			return;
		}

		if (conversationContainerRef.current) {
			const scrollValue = conversationContainerRef.current.scrollHeight / 4;
			conversationContainerRef.current.scrollTop = scrollValue;
		}
		// @ts-expect-error - Leaving code as it is
		useStore.getState().chat.toggleScrollFetch(false);
	};

	useEffect(() => {
		setIsChatTurnedOff(chatBlocked);
	}, [chatBlocked]);

	// Scroll down to bottom when window is opened
	useEffect(() => {
		if (scrollRef && scrollRef.current) {
			setTimeout(() => {
				scrollRef.current?.scrollIntoView({ block: "end", behavior: "smooth" });
			}, 1000);
		}
	}, []);

	useEffect(() => {
		handleNewWindowData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (classroomDetails && Object.entries(classroomDetails).length) {
			const [isActiveClass] = classroomActiveInfoV2(classroomDetails);
			setClassActive(isActiveClass);
		}
	}, [classroomDetails]);

	useEffect(() => {
		setMessages([]);

		let unsubMessages = (): void => {};
		let unsubActiveWindowData = (): void => {};

		const getMessages = async (): Promise<void> => {
			// @ts-expect-error - Leaving code as it is
			await useStore.getState().chat.fetchMessages(
				{
					classroomId: classroomIdForChat,
					threadKey,
				},
				isEmbedded
			);

			// @ts-expect-error - Leaving code as it is
			const newThread = useStore.getState().chat.threads.data[threadKey];
			if (newThread) {
				// subscription wont kick in new message handler if thread has already been fetched and has no changes
				handleNewMessagesReceived();
				handleNewWindowData();
			}

			unsubMessages = useStore.subscribe(
				// @ts-expect-error - Leaving code as it is
				(state) => state.chat.threads.data[threadKey],
				// @ts-expect-error - Leaving code as it is
				(messages) => handleNewMessagesReceived(messages),
				shallow
			);

			unsubActiveWindowData = useStore.subscribe(
				// @ts-expect-error - Leaving code as it is
				(state) => state.activeClassroomWindowStore,
				() => handleNewWindowData(),
				shallow
			);
		};

		getMessages();

		return () => {
			unsubMessages();
			unsubActiveWindowData();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [threadKey]);

	const handleNewWindowData = async (): Promise<void> => {
		const selectedStudentName = useStore.getState().chat.selectedStudentName;
		const windowData = getWindowDataForUser(selectedStudentName);
		setStudentDeviceWindowData(windowData || null);
	};

	const sendMessage = (messageToResend?: ChatMessage): void => {
		if (messageToResend) {
			// @ts-expect-error - Leaving code as it is
			useStore.getState().chat.sendMessage(
				{
					...messageToResend,
					username,
				},
				false
			);
			return;
		}

		if (!inputMessage || !inputMessage.trim() || inputMessage.length >= 1000) {
			return;
		}

		let participants: string[] = [];

		// @ts-expect-error - Leaving code as it is
		if (classroomDetails && classroomDetails.owners) {
			// @ts-expect-error - Leaving code as it is
			participants = [...classroomDetails.owners];
		}

		// @ts-expect-error - Leaving code as it is
		const id = useStore.getState().chat.generateMessageId();
		// @ts-expect-error - Leaving code as it is
		useStore.getState().chat.sendMessage({
			message: inputMessage,
			username,
			classroomId: classroomIdForChat,
			threadKey,
			id,
			participants,
		});

		setInputMessage("");
	};

	const handleMessageSend = (): void => {
		sendMessage();
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>): void => {
		if (event.key === "Enter") {
			event.preventDefault();
			handleMessageSend();
		}
	};

	const handleOnChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
		setInputMessage(event.target.value);
	};

	const handleOnScroll = (event: React.UIEvent<HTMLDivElement>): void => {
		const target = event.target as HTMLDivElement;
		if (target.scrollTop === 0) {
			// @ts-expect-error - Leaving code as it is
			useStore.getState().chat.toggleScrollFetch(true);
			// @ts-expect-error - Leaving code as it is
			useStore.getState().chat.fetchMessages({ classroomId: classroomIdForChat, threadKey });
		}
	};

	const getAvatarColor = (message: ChatMessage, classroom?: ClassroomDetails): string | null => {
		// @ts-expect-error - Leaving code as it is
		if (!classroom || !classroom.owners) {
			return null;
		}

		// @ts-expect-error - Leaving code as it is
		const teachers = classroom.owners.filter((owner) => owner !== localStorage.getItem("USERNAME"));
		// @ts-expect-error - Leaving code as it is
		const colorIndex = teachers.findIndex((teacher) => teacher === message.sender.username);

		if (colorIndex !== -1) {
			// @ts-expect-error - Leaving code as it is
			return avatarColors[colorIndex + 1];
		}

		return null;
	};

	if (
		// @ts-expect-error - Leaving code as it is
		useStore.getState().chat.threads.isFetching &&
		// @ts-expect-error - Leaving code as it is
		!useStore.getState().chat.scrollFetch &&
		// @ts-expect-error - Leaving code as it is
		!useStore.getState().chat.threads.isUpdateFetch
	) {
		// TODO: replace with proper loading states
		// @ts-expect-error - Leaving code as it is
		return <NoEntries title="Loading messages" key="loading" />;
	}

	const renderedMessages: React.ReactNode[] = [];
	const monitoringReminder = (
		<div key="monitoringReminder" className="ClassroomChatMessagePane_monitoringReminder">
			{t("classroomChat.monitoringReminder")}
		</div>
	);

	if (messages) {
		const filteredMessages = messages.filter((message) => !message.isFakeMessage);
		let formattedMessages = filteredMessages;

		filteredMessages.forEach((message, index) => {
			let avatarColor = "";
			const isOwner = message.sender.username === localStorage.getItem("USERNAME");

			formattedMessages = formatMsgTs(index, filteredMessages);

			if (!isOwner) {
				const isStudent = message.sender.username === username;
				// @ts-expect-error - Leaving code as it is
				avatarColor = isStudent ? avatarColors[0] : getAvatarColor(message, classroomDetails);
			}

			if (index > 0 && !isMonitoringReminderShown) {
				const date1 = new Date(filteredMessages[index - 1].timestamp);
				const date2 = new Date(filteredMessages[index].timestamp);
				// @ts-expect-error - Leaving code as it is
				if (date1.getDay() > date2.getDay() || date1.getMonth() > date2.getMonth() || date1.getYear() > date2.getYear()) {
					renderedMessages.push(monitoringReminder);
					isMonitoringReminderShown = true;
				}
			}

			renderedMessages.push(
				<MessageItem
					avatarColor={avatarColor}
					key={formattedMessages[index].id}
					message={formattedMessages[index]}
					resendMessage={sendMessage}
					isOwner={isOwner}
				/>
			);
		});
	}

	if (!renderedMessages.length) {
		renderedMessages.push(
			<NoEntries title={t("classroomChat.noMessagesTitle")} key="no-messages">
				{monitoringReminder}
			</NoEntries>
		);
	}

	return (
		<div className="ClassroomChatMessagePane_container">
			<div className="ClassroomChatMessagePane_conversationContainer" onScroll={handleOnScroll} ref={conversationContainerRef}>
				{renderedMessages.reverse()}
				<div ref={scrollRef} />
			</div>
			{isStudentClaimed ? (
				<ChatInput
					classActive={classActive}
					isChatTurnedOff={isChatTurnedOff}
					studentDeviceWindowData={studentDeviceWindowData}
					handleOnChange={handleOnChange}
					handleKeyDown={handleKeyDown}
					inputMessage={inputMessage}
					handleMessageSend={handleMessageSend}
					chatError={useStore.getState().chat.chatError}
					handleChatEnable={handleChatEnable || (() => {})}
				/>
			) : (
				<div className="ClassroomChatMessagePane_studentClaimNotice" data-testid="student-claim-chat-unavailable" role="status">
					<p>Chat is unavailable as student is being managed by another class.</p>
					<p className="ClassroomChatMessagePane_studentClaimNotice_cta">Claim Student to manage them in your class.</p>
				</div>
			)}
		</div>
	);
};

export default MessagePane;
